import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { removeFiles, setAppProperty } from "../../../../redux/actions";
import BackAnchor from "../../../../shared/components/BackAnchor";
import { addToGoogleDataLayer } from "../../../../shared/const/utls";
import ContinueCancelButtons from "../../../../shared/components/ContinueCancelButtons";
import FileUpload from "../../../../shared/components/FileUpload";
import Loader from "../../../../shared/components/Loader";
import {
  bytesToSize,
  fileToBase64,
  getFileTypeFromMimeType
} from "../../../../helpers/fileHelpers";
import moment from "moment";
import { faBeer, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const HEATING_BILL = "heatingBill";

const UploadHeatingView = ({ t, i18n }) => {
  const dispatch = useDispatch();

  const datesData = useSelector((state) => state.datesReducer);
  const [filesLoading, setFilesLoading] = useState(false);
  const [fileError, setFileError] = useState("");
  const userData = useSelector((state) => state.app);

  let currentLanguageDates;
  if (datesData) {
    currentLanguageDates = datesData[i18n.language];
  }
  const billUploadPoint2 = t("upload_bill.point_2_3")
    .replace("#START_YEAR", moment(currentLanguageDates?.currentHarpDate, 'MMMM D YYYY').format('D MMMM YYYY'))
    .replace("#END_YEAR", moment(currentLanguageDates?.endDate, 'MMMM D YYYY').format('D MMMM YYYY'));

  const navigate = useNavigate();
  const params = useParams();
  const { search } = useLocation();
  const url = new URLSearchParams(search);
  const isSummary = url?.get("isSummary");

  const heatingBillFiles = useSelector((state) => state.app.heatingBill);

  const createDownloadUrl = (base64Data, fileName) => {
    const [meta, data] = base64Data.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mime });
    return URL.createObjectURL(blob);
  };


  const handleFileChange = (fileList) => {
    const file = fileList[0];

    if (fileList.length === 0) {
      return;
    }

    setFilesLoading(true);

    fileToBase64(file)
      .then((rawData) => {
        const base64Data = rawData.split(",")[1];
        const fileData = {
          changeType: "SAVE",
          data: base64Data,
          name: file.name,
          size: bytesToSize(file.size),
          type: getFileTypeFromMimeType(file.type),
          url: createDownloadUrl(rawData, file.name)
        };
        dispatch(setAppProperty(HEATING_BILL, [fileData]));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setFilesLoading(false);
      });
  };
  const handleFileDelete = (fileName) => {
    const updatedFiles = heatingBillFiles.filter(file => file.name !== fileName);
    dispatch(setAppProperty(HEATING_BILL, updatedFiles));
  };

  const disableGApush = useMemo(() => {
    return JSON.parse(params?.disableGA || false);
  }, [params]);

  useEffect(() => {
    if (!disableGApush) {
      try {
        addToGoogleDataLayer(
          "upload-heating-bill",
          "HARP Application",
          i18n.language
        );
        window.scrollTo(0, 0);
      } catch (e) {
        console.log(e);
      }
    }
  }, [i18n.language, disableGApush]);

  const onNext = () => {
    try {
      if (heatingBillFiles?.length > 0) {
        if (isSummary) {
          navigate(`/${i18n.language}${t("routes.review_summary")}`);
        } else {
          navigate(`/${i18n.language}${t("routes.consent_to_share")}`);
        }
      } else {
        navigate(`/${i18n.language}${t("routes.without_uploading_bill")}${isSummary ? `?isSummary=true` : ''}`);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleReduxCleanup = () => {
    if (userData?.heatingBill.length > 0) {
      dispatch(removeFiles('heatingBill', []));
    }
  }

  return (
    <div>
      <div className="container">
        <main>
          <article>
            <div className="row" style={{ paddingTop: "1em" }}></div>
            <BackAnchor backPath={'routes.heat_your_home'} onBackClick={handleReduxCleanup} />
            <div className="row">
              <div className="col-xs-12 ">
                <div className="col-xs-11 col-sm-8">
                  <h1>{t("upload_bill.header")}</h1>
                </div>
                <div className="col-xs-11 col-sm-8 col-md-12">
                  <p>{t("upload_bill.para_1")}</p>
                  <span>{t("upload_bill.para_2")}</span>
                  <ul style={{ "marginTop": 0 }}>
                    <li>{t("upload_bill.point_1")}</li>
                    <li>{t("upload_bill.point_2")} </li>
                    <li>{t("upload_bill.point_3")} </li>
                  </ul>
                  <span>{t("upload_bill.para_3")}</span>
                  <ul style={{ "marginTop": 0 }}>
                    <li>{t("upload_bill.point_2_1")}</li>
                    <li>{t("upload_bill.point_2_2")}</li>
                    <li>{billUploadPoint2} </li>
                  </ul>
                  <p> {t("upload_bill.para_4")}</p>
                </div>
                <div className="col-xs-11 col-sm-8">
                  <div>
                    <b>{t("upload_bill.upload_proof_heading")}</b>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FileUpload handleFileChange={handleFileChange} />
                    <Loader isLoading={filesLoading} />
                  </div>
                  <div style={{ fontSize: ".8rem" }}>
                    {t("upload_bill.upload_type")}
                  </div>
                  <div style={{ fontSize: ".8rem" }}>
                    {t("upload_bill.upload_size")}
                  </div>
                  <div style={{ marginTop: "20px" }}>
                    <b>
                      <b>{t("upload_bill.uploaded_proof_heading")}</b>
                    </b>
                  </div>

                  <ul>
                    {heatingBillFiles?.map((file, i) => (
                      <li key={i} style={{ marginBottom: "10px" }}>
                        <span style={{ color: "#24569f", textDecoration: "underline" }}>
                          <a
                            href={file.url}
                            download={file.name}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#24569f", textDecoration: "underline" }}>
                            {file.name}
                          </a>
                        </span>
                        <span style={{ color: "#24569f", marginLeft: "10px" }}> {file.size}</span>
                        <button
                          onClick={() => handleFileDelete(file.name)}
                          style={{
                            marginLeft: "10px",
                            border: "none",
                            background: "none",
                            color: "red",
                            fontSize: "1.2rem",
                            cursor: "pointer"
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} alt="Delete" style={{ width: "16px", height: "14px", marginRight: "7px", color: "#24569f" }} />
                          <span style={{ color: "#24569f", fontSize: "14px" }}>{t("other_adult_living_form.delete")}</span>
                        </button>
                      </li>

                    ))}
                  </ul>
                </div>
              </div>
              <ContinueCancelButtons continueClick={onNext} />
            </div>
          </article>
        </main>
      </div>
    </div>
  );
};

export default withTranslation("common")(UploadHeatingView);
